import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { useAuth } from '../lib/auth';

const DOMAINS = [
  { id: 'patient_care', title: 'Patient Care / Clinical Skills', bullets: ['Conducts focused history and physical examination','Selects appropriate diagnostic and symptom management strategies','Develops and implements care plans based on patient goals','Performs essential palliative care procedures','Educates patients and families on care options and planning','Demonstrates sound clinical judgment','Maintains clear, accurate, and timely documentation'] },
  { id: 'medical_knowledge', title: 'Medical Knowledge', bullets: ['Possesses appropriate fund of knowledge in hospice and palliative care','Applies knowledge to symptoms management, end-of-life care and goals of care planning'] },
  { id: 'communication', title: 'Interpersonal & Communication Skills', bullets: ['Interacts well with staff, faculty & colleagues','Demonstrates compassion for patients and their families','Presents patient cases effectively and concisely','Collaborates effectively as member or leader of the interdisciplinary team','Is able to act as consultant to other health professionals'] },
  { id: 'practice_learning', title: 'Practice-Based Learning Skills', bullets: ['Actively seeks feedback to improve clinical practice','Engages in self-directed learning to enhance hospice and palliative care knowledge','Critically appraises literature and applies evidence-based practices to patient care','Reflects on challenging cases to improve communication and clinical decision-making'] },
  { id: 'system_practice', title: 'System-Based Practice Skills', bullets: ['Understands and utilizes healthcare resources effectively to support patient and family needs','Navigates hospice and palliative care systems, including outpatient, emergency department, and home-based services','Collaborates with interdisciplinary teams to ensure coordinated, patient-centered care'] },
  { id: 'professionalism', title: 'Professionalism', bullets: ['Punctual, works hard and helps others','Fulfills assigned clinical and on call responsibilities (Reliable)','Demonstrates dependability and commitment','Demonstrates respect, compassion and honesty',"Adherent to center's policies and procedures"] },
];

const QUARTERS = [
  { value: 'Q1 (July – September)',  label: 'Q1 — July to September' },
  { value: 'Q2 (October – December)', label: 'Q2 — October to December' },
  { value: 'Q3 (January – March)',    label: 'Q3 — January to March' },
  { value: 'Q4 (April – June)',       label: 'Q4 — April to June' },
];

const SCALE = [
  { range: 'NA', label: 'Not Applicable' },
  { range: '1–4', label: 'Unsatisfactory' },
  { range: '5–6', label: 'Satisfactory' },
  { range: '7–8', label: 'Good' },
  { range: '9–10', label: 'Outstanding' },
];

const scoreLabel = (n) => !n ? '' : n <= 4 ? 'Unsatisfactory' : n <= 6 ? 'Satisfactory' : n <= 8 ? 'Good' : 'Outstanding';
const scoreColor = (n) => !n ? '#E0DDD8' : n <= 4 ? '#E85252' : n <= 6 ? '#F4A83A' : n <= 8 ? '#4A9D6F' : '#1A3A5C';

export default function EvaluationForm({ onSaved, onCancel, isMobile }) {
  const { user, profile } = useAuth();
  const [fellows, setFellows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [form, setForm] = useState({
    fellow_id: '', fellow_name: '', fellow_email: '',
    rotation_period: '', evaluation_date: new Date().toISOString().split('T')[0],
    score_patient_care: '', score_medical_knowledge: '', score_communication: '',
    score_practice_learning: '', score_system_practice: '', score_professionalism: '',
    comment_patient_care: '', comment_medical_knowledge: '', comment_communication: '',
    comment_practice_learning: '', comment_system_practice: '', comment_professionalism: '',
  });

  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email').eq('role', 'fellow')
      .then(({ data }) => setFellows(data || []));
  }, []);

  const totalScore = DOMAINS.reduce((sum, d) => sum + (parseInt(form[`score_${d.id}`]) || 0), 0);
  const pct = Math.round((totalScore / 60) * 100);

  const handleFellowChange = (id) => {
    const f = fellows.find(f => f.id === id);
    setForm(prev => ({ ...prev, fellow_id: id, fellow_name: f?.full_name || '', fellow_email: f?.email || '' }));
  };

  const submit = async () => {
    if (!form.fellow_id)        { setError('Please select a fellow.'); return; }
    if (!form.rotation_period)  { setError('Please select the evaluation quarter.'); return; }
    const missing = DOMAINS.filter(d => !form[`score_${d.id}`]);
    if (missing.length > 0)     { setError(`Please score all domains. Missing: ${missing.map(d => d.title).join(', ')}`); return; }
    setLoading(true); setError('');
    const { error: e } = await supabase.from('evaluations').insert({
      ...form,
      evaluator_id: user.id, evaluator_name: profile?.full_name,
      score_patient_care: parseInt(form.score_patient_care),
      score_medical_knowledge: parseInt(form.score_medical_knowledge),
      score_communication: parseInt(form.score_communication),
      score_practice_learning: parseInt(form.score_practice_learning),
      score_system_practice: parseInt(form.score_system_practice),
      score_professionalism: parseInt(form.score_professionalism),
    });
    if (e) { setError(e.message); setLoading(false); return; }
    setLoading(false); onSaved();
  };

  return (
    <div style={{ background: '#F7F4F0', minHeight: '100vh', paddingBottom: isMobile ? 80 : 40 }}>
      <div style={{ background: '#1A3A5C', padding: isMobile ? '14px 16px' : '16px 40px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#7EB8D4', fontSize: 14, cursor: 'pointer', padding: 0 }}>← Back</button>
        <span style={{ fontFamily: "'DM Serif Display'", fontSize: isMobile ? 16 : 20, color: '#F5F0E8' }}>Trainee Evaluation Form</span>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? 16 : '28px 40px' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', textAlign: 'center', borderTop: '4px solid #1A3A5C' }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Academic Affairs Office · Physician Education Center</div>
          <div style={{ fontFamily: "'DM Serif Display'", fontSize: 20, color: '#1A3A5C', marginBottom: 4 }}>Physician Trainee General Evaluation Form</div>
          <div style={{ fontSize: 12, color: '#888' }}>Department of Hospice and Palliative Medicine @ KHCC</div>
        </div>

        {error && <div style={{ background: '#FFF0F0', border: '1px solid #F4A8A8', color: '#8A2D2D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontFamily: "'DM Serif Display'", fontSize: 16, color: '#1A1A1F', marginBottom: 16 }}>Evaluation Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <div>
              <label style={ls.label}>Trainee's Name *</label>
              <select style={ls.input} value={form.fellow_id} onChange={e => handleFellowChange(e.target.value)}>
                <option value=''>— Select Fellow —</option>
                {fellows.map(f => <option key={f.id} value={f.id}>{f.full_name}</option>)}
              </select>
            </div>
            <div>
              <label style={ls.label}>Attending Name</label>
              <input style={{ ...ls.input, background: '#F5F5F5' }} value={profile?.full_name || ''} readOnly />
            </div>
            <div>
              <label style={ls.label}>Evaluation Quarter *</label>
              <select style={ls.input} value={form.rotation_period} onChange={e => setForm(f => ({ ...f, rotation_period: e.target.value }))}>
                <option value=''>— Select Quarter —</option>
                {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
              </select>
            </div>
            <div>
              <label style={ls.label}>Evaluation Date</label>
              <input type="date" style={ls.input} value={form.evaluation_date} onChange={e => setForm(f => ({ ...f, evaluation_date: e.target.value }))} />
            </div>
          </div>
        </div>

        <div style={{ background: '#EEF3FA', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1A3A5C' }}>Evaluation Scale:</span>
          {SCALE.map(s => <span key={s.range} style={{ fontSize: 12, color: '#555' }}><strong>{s.range}</strong> = {s.label}</span>)}
        </div>

        {DOMAINS.map((domain, idx) => (
          <div key={domain.id} style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div style={{ flex: 1, paddingRight: 16 }}>
                <div style={{ fontFamily: "'DM Serif Display'", fontSize: 16, color: '#1A3A5C' }}>{idx + 1}. {domain.title}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Maximum score: 10</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 70 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: scoreColor(parseInt(form[`score_${domain.id}`])), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: "'DM Serif Display'", fontSize: 24, color: '#fff', fontWeight: 700 }}>{form[`score_${domain.id}`] || '?'}</span>
                </div>
                {form[`score_${domain.id}`] && <div style={{ fontSize: 10, color: scoreColor(parseInt(form[`score_${domain.id}`])), fontWeight: 600 }}>{scoreLabel(parseInt(form[`score_${domain.id}`]))}</div>}
              </div>
            </div>
            <ul style={{ margin: '0 0 14px 0', paddingLeft: 20 }}>
              {domain.bullets.map((b, i) => <li key={i} style={{ fontSize: 13, color: '#555', lineHeight: 1.7, marginBottom: 2 }}>{b}</li>)}
            </ul>
            <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => {
                const selected = parseInt(form[`score_${domain.id}`]) === n;
                const bg = n <= 4 ? '#E85252' : n <= 6 ? '#F4A83A' : n <= 8 ? '#4A9D6F' : '#1A3A5C';
                return <button key={n} onClick={() => setForm(f => ({ ...f, [`score_${domain.id}`]: n }))} style={{ flex: 1, padding: isMobile ? '10px 0' : '8px 0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: isMobile ? 14 : 13, fontWeight: selected ? 700 : 400, background: selected ? bg : '#F0EDE8', color: selected ? '#fff' : '#888' }}>{n}</button>;
              })}
            </div>
            <textarea value={form[`comment_${domain.id}`]} rows={2} placeholder="Comments (optional)…" onChange={e => setForm(f => ({ ...f, [`comment_${domain.id}`]: e.target.value }))} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E0DDD8', borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans'", resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        ))}

        <div style={{ background: '#1A3A5C', borderRadius: 12, padding: '20px 28px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: '#7EB8D4', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Total Score</div>
            <div style={{ fontFamily: "'DM Serif Display'", fontSize: 48, color: '#F5F0E8', lineHeight: 1 }}>{totalScore}<span style={{ fontSize: 22, opacity: 0.5 }}>/60</span></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#7EB8D4', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Percentage</div>
            <div style={{ fontFamily: "'DM Serif Display'", fontSize: 48, color: '#F5F0E8', lineHeight: 1 }}>{pct}<span style={{ fontSize: 22, opacity: 0.5 }}>%</span></div>
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, color: pct >= 90 ? '#7EB8D4' : pct >= 70 ? '#A8DCC0' : pct >= 50 ? '#F4DFA8' : '#F4A8A8' }}>
              {pct >= 90 ? '★ Outstanding' : pct >= 70 ? '✓ Good' : pct >= 50 ? '~ Satisfactory' : '✗ Unsatisfactory'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={onCancel} style={{ padding: '13px 20px', background: '#EEE', border: 'none', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: "'DM Sans'" }}>Cancel</button>
          <button onClick={submit} disabled={loading} style={{ flex: 1, padding: '13px', background: '#1A3A5C', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.7 : 1, fontFamily: "'DM Sans'" }}>
            {loading ? 'Submitting…' : '✓ Submit Evaluation'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ls = {
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6, letterSpacing: 0.4, textTransform: 'uppercase' },
  input: { width: '100%', padding: '11px 13px', border: '1.5px solid #E0DDD8', borderRadius: 8, fontSize: 14, color: '#1A1A1F', background: '#fff', outline: 'none', fontFamily: "'DM Sans'", boxSizing: 'border-box' },
};
